import {
  CSSProperties,
  forwardRef,
  MutableRefObject,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IAuthUser } from 'auth/types';
import { Form, FormField } from 'core/components';
import { FormGroupWrapper } from 'core/elements';
import { InputType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext } from 'core/states';
import { getDateOptions, objectMapToSelectOptions } from 'core/utils';
import { DEFAULT_SHIPPING_METHOD_NAME } from 'corporate/constants';
import {
  CorporateLocationFormField,
  FreightTermFormField,
  ShippingMethodFormField,
} from 'corporate/elements';
import { CorporateLocationType } from 'corporate/enums';
import { corporateService } from 'corporate/services';
import { IVerticalMarket } from 'corporate/types';
import { ExternalCustomerNumberField } from 'customer/components';
import { ICreateCustomerQuoteFromQuoteForm as IDuplicateQuoteForm } from 'quotes/components/QuotePartAddToQuote';
import { PricingDateFormField, PricingStrategyFormField } from 'quotes/elements';
import { QuoteEntityType, QuoteStatus } from 'quotes/enums';
import { getOrderTypeLabels } from 'quotes/utils';

import { CustomerContactFormFields } from './CustomerContactFormFields';
import { ProjectFormField } from './ProjectFormField';
import { ProspectQuickQuoteDefaults } from './ProspectQuickQuoteDefaults';
import { ShipSelectionDateFields } from './ShipSelectionDateFields';
import { ICustomerQuoteUpsertForm, IQuickQuoteContactPreload } from './types';
import { getElementToFocus, getStatusOptions } from './utils';

export type TCustomerQuoteUpsertDisabledFields = {
  project?: boolean;
  jobName?: boolean;
  contact?: boolean;
  shipToLocation?: boolean;
  billToLocation?: boolean;
  location?: boolean;
  shippingLocation?: boolean;
};

export type TCustomerQuoteUpsertHiddenFields = {
  project?: boolean;
  jobName?: boolean;
  contact?: boolean;
  shipToLocation?: boolean;
  billToLocation?: boolean;
  location?: boolean;
  setBillToAsDefaultForContact?: boolean;
  setShipToAsDefaultForContact?: boolean;
};

const DEFAULT_HIDDEN_FIELDS: TCustomerQuoteUpsertHiddenFields = {
  project: false,
  jobName: false,
  contact: false,
  location: false,
};

// Surfaces that post only `mapCreateCustomerQuoteForm(...).quote` drop the root-level
// default-address flags, so these checkboxes would be dead controls there.
// Spreads the defaults because a caller-supplied object replaces them wholesale.
export const SET_AS_DEFAULT_FOR_CONTACT_HIDDEN_FIELDS: TCustomerQuoteUpsertHiddenFields = {
  ...DEFAULT_HIDDEN_FIELDS,
  setBillToAsDefaultForContact: true,
  setShipToAsDefaultForContact: true,
};

interface IProps<T extends ICustomerQuoteUpsertForm | IDuplicateQuoteForm> {
  isOpen: boolean;
  isAlternateVersionEnabled: boolean;
  onSubmit?: (reset?: UseFormReset<T>) => (data: T) => void;
  onClose: (reset: UseFormReset<T>) => void;
  user: IAuthUser;
  defaultValues: Partial<T>;
  disabledFields?: TCustomerQuoteUpsertDisabledFields;
  hiddenFields?: TCustomerQuoteUpsertHiddenFields;
  entityType: QuoteEntityType;
  isEdit?: boolean;
  extraFields?: JSX.Element;
  title?: string;
  subtitle?: string;
  withoutForm?: boolean;
  withoutNotification?: boolean;
  stickySxProps?: {
    header?: CSSProperties;
    notification?: CSSProperties;
  };
  preloadContact?: IQuickQuoteContactPreload;
  enableProspectQuickQuote?: boolean;
}

// Call on every run of the effect, above the isOpen guard: tracking while the drawer is closed
// absorbs handleOnClose's reset() and defaultValues changes between opens, so the next open sees
// contactChanged: false instead of reading a prefilled contact as a fresh selection.
const readAndTrackChange = <T,>(value: T, ref: MutableRefObject<T>): boolean => {
  const hasChanged = value !== ref.current;
  ref.current = value;

  return hasChanged;
};

export const CustomerQuoteUpsertForm = forwardRef(function CustomerQuoteUpsertForm<
  T extends ICustomerQuoteUpsertForm | IDuplicateQuoteForm,
>(
  {
    isOpen,
    isAlternateVersionEnabled = false,
    onSubmit,
    onClose,
    user,
    defaultValues,
    disabledFields = {
      project: false,
      jobName: false,
      contact: false,
      location: false,
    },
    hiddenFields = DEFAULT_HIDDEN_FIELDS,
    entityType = QuoteEntityType.QUOTE,
    isEdit = false,
    extraFields = <></>,
    title = null,
    subtitle = null,
    withoutForm = false,
    withoutNotification = false,
    stickySxProps,
    preloadContact,
    enableProspectQuickQuote = false,
  }: IProps<T>,
  ref
) {
  const { t } = useTranslation();

  const { watch, trigger, getValues } = useFormContext();
  const { setFocusElementName } = useFocusInputFunctionality();

  const projectValue = watch('project');
  const contactValue = watch('contact');

  const lastContactRef = useRef(contactValue);

  const isBillToLocationHidden = Boolean(hiddenFields.billToLocation);
  const isShipToLocationHidden = Boolean(hiddenFields.shipToLocation);
  const isBillToLocationDisabled = Boolean(disabledFields.billToLocation);
  const isShipToLocationDisabled = Boolean(disabledFields.shipToLocation);

  useEffect(() => {
    const contactChanged = readAndTrackChange(contactValue, lastContactRef);

    if (!isOpen) {
      return;
    }

    const elementToFocus = getElementToFocus({
      isEdit,
      entityType,
      isAlternateVersionEnabled,
      contactChanged,
      hasContact: Boolean(contactValue),
      hasProject: Boolean(projectValue),
      // getValues instead of watch: the locations only matter on the run the
      // contact changes, and their own changes must not re-trigger focus.
      needsBillToLocation:
        !isBillToLocationHidden && !isBillToLocationDisabled && !getValues('billToLocation'),
      needsShipToLocation:
        !isShipToLocationHidden && !isShipToLocationDisabled && !getValues('shipToLocation'),
    });

    if (elementToFocus) {
      setFocusElementName(elementToFocus);
    }
  }, [
    isOpen,
    setFocusElementName,
    isEdit,
    entityType,
    isAlternateVersionEnabled,
    projectValue,
    contactValue,
    getValues,
    isBillToLocationHidden,
    isShipToLocationHidden,
    isBillToLocationDisabled,
    isShipToLocationDisabled,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      triggerFormSubmit: () => {
        return new Promise((resolve, reject) => {
          trigger()
            .then((isValid) => {
              if (isValid) {
                resolve(getValues());
              } else {
                reject();
              }
            })
            .catch(reject);
        });
      },
    }),
    [getValues, trigger]
  );

  const requiredOrderFields = isAlternateVersionEnabled
    ? AlternateVersionHelper.isDdpOrderIntegrationEnabled({ user })
    : user?.companySettings?.coreOrderIntegrationEnabled;

  const disabledExternalCustomerNumber =
    !isEdit && !(watch('project')?.id && watch('jobName') && watch('contact')?.userId);

  const formTitle =
    title ||
    (isEdit
      ? t('titleEditCustomerQuote', { entityType })
      : t('titleCreateCustomerQuote', { entityType }));

  const externalCustomerNumberPayload = {
    jobName: watch('jobName') || '',
    projectId: watch('project')?.id || '',
    customerUserId: watch('contact')?.userId || 0,
    alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
  };

  const projectField = useMemo(
    () =>
      !hiddenFields.project ? (
        <ProjectFormField
          defaultDtdProject={!isAlternateVersionEnabled}
          hidden={hiddenFields.project}
          disabled={disabledFields.project}
        />
      ) : null,
    [hiddenFields.project, isAlternateVersionEnabled, disabledFields.project]
  );

  const jobNameField = useMemo(
    () => (
      <FormField
        name="jobName"
        type={InputType.TEXT_INPUT}
        hidden={hiddenFields.jobName}
        inputProps={{
          label: t('labelJobName'),
          disabled: disabledFields.jobName,
          required: true,
        }}
      />
    ),
    [t, hiddenFields.jobName, disabledFields.jobName]
  );

  const fieldsStack = (
    <>
      {enableProspectQuickQuote && QuoteEntityType.QUOTE === entityType && (
        <ProspectQuickQuoteDefaults />
      )}

      {/* For NON-ALTERNATE users: Project and JobName first */}
      {!isAlternateVersionEnabled && (
        <>
          {projectField}
          {jobNameField}
        </>
      )}

      <CustomerContactFormFields
        entityType={entityType}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        hiddenFields={hiddenFields}
        initialSelectedContactGroup={defaultValues?.contact?.contactGroup}
        disabledFields={disabledFields}
        // QUOTE only, same gate as ProspectQuickQuoteDefaults above. The preload bypasses
        // onChangeContact's AR gate, which is what kept bill-to and ship-to symmetric; the
        // bill-to handler still refuses a no-AR location for an ORDER while the ship-to
        // handler has no such gate, so preloading an ORDER would half-fill it.
        preloadContact={QuoteEntityType.QUOTE === entityType ? preloadContact : undefined}
      />

      {/* For ALTERNATE users: Project and JobName after locations */}
      {isAlternateVersionEnabled && (
        <>
          {projectField}
          {jobNameField}
        </>
      )}

      {QuoteEntityType.QUOTE === entityType && (
        <>
          <FormField
            name="closeConfidence"
            type={InputType.NUMBER_INPUT}
            inputProps={{
              label: t('labelCloseConfidence'),
              required: true,
              slotProps: {
                input: {
                  min: 0,
                  max: 100,
                  allowedDecimalSeparators: [],
                  decimalScale: 0,
                  suffix: '%',
                },
              },
            }}
          />

          {isEdit && (
            <>
              <FormField
                name="status"
                type={InputType.SELECT}
                inputProps={{
                  label: t('labelStatus'),
                  required: true,
                  options: getStatusOptions((defaultValues as ICustomerQuoteUpsertForm)?.status),
                  disabled:
                    QuoteStatus.EDITABLE_BY_CUSTOMER ===
                    (defaultValues as ICustomerQuoteUpsertForm)?.status,
                }}
              />

              {QuoteStatus.LOST === watch('status') && (
                <FormField
                  name="quoteStatusLostNote"
                  type={InputType.TEXT_INPUT}
                  inputProps={{
                    label: t('labelReasonQuoteLose'),
                    required: true,
                  }}
                />
              )}
            </>
          )}

          <FormField
            name="closeDate"
            type={InputType.SELECT}
            inputProps={{
              label: t('labelCloseDate'),
              required: true,
              options: getDateOptions(),
            }}
          />
        </>
      )}

      {QuoteEntityType.ORDER === entityType && (
        <>
          {!isAlternateVersionEnabled && (
            <ExternalCustomerNumberField
              requestPayload={externalCustomerNumberPayload}
              required={requiredOrderFields}
              disabled={disabledExternalCustomerNumber}
              typeChoiceEnabled={!isAlternateVersionEnabled}
            />
          )}

          <FormField
            name="purchaseOrderNumber"
            type={InputType.TEXT_INPUT}
            inputProps={{
              label: t('labelPurchaseOrderNumber'),
              required: requiredOrderFields,
            }}
          />
        </>
      )}

      {isAlternateVersionEnabled && QuoteEntityType.ORDER === entityType && (
        <>
          <FormField
            name="orderType"
            type={InputType.SELECT}
            inputProps={{
              label: t('labelOrderType'),
              required: requiredOrderFields,
              options: objectMapToSelectOptions(getOrderTypeLabels()),
            }}
          />

          <ShipSelectionDateFields required={requiredOrderFields} />

          <ShippingMethodFormField
            name="shippingMethod"
            defaultName={DEFAULT_SHIPPING_METHOD_NAME}
            initialValue={defaultValues?.shippingMethod}
            required={requiredOrderFields}
          />

          <FreightTermFormField name="freightTerm" required={requiredOrderFields} />
        </>
      )}

      <FormField
        name="verticalMarket"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label: t('labelVerticalMarket'),
          required: Boolean(user.companySettings?.verticalMarketRequired),
          getOptions: corporateService.getVerticalMarketsAutocomplete,
          getLabel: (option: IVerticalMarket) => option?.name,
          enabledOnFocus: false,
        }}
      />

      {QuoteEntityType.QUOTE === entityType && (
        <FormGroupWrapper name="validDates">
          <FormField
            name="validSince"
            type={InputType.DATE_INPUT}
            inputProps={{
              label: t('labelValidSince'),
              maxDate: watch('validUntil'),
            }}
          />
          <FormField
            name="validUntil"
            type={InputType.DATE_INPUT}
            inputProps={{
              label: t('labelValidUntil'),
              minDate: watch('validSince'),
              required: true,
            }}
          />
        </FormGroupWrapper>
      )}

      {isAlternateVersionEnabled && <PricingDateFormField label={t('labelDefaultPricingDate')} />}

      {isAlternateVersionEnabled && (
        <PricingStrategyFormField label={t('labelDefaultPricingStrategy')} />
      )}

      <CorporateLocationFormField
        name="location"
        label={t(`label${entityType}Location`)}
        hidden={hiddenFields?.location}
        disabled={disabledFields?.location}
        required={!isEdit}
        enabledOnFocus={false}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        type={CorporateLocationType.SALES}
      />

      <CorporateLocationFormField
        name="shippingLocation"
        label={t('labelDefaultShippingLocation')}
        disabled={disabledFields?.shippingLocation}
        required
        enabledOnFocus={false}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        type={CorporateLocationType.SHIPPING}
      />

      {extraFields}
    </>
  );

  return (
    <Form
      title={formTitle}
      subtitle={subtitle}
      isOpen={isOpen}
      onSubmit={onSubmit}
      onClose={onClose}
      cancelBtnLabel={t('buttonLabelCancel')}
      inline={withoutForm}
      withoutButtons={withoutForm}
      withoutNotification={withoutNotification}
      stickySxProps={stickySxProps}
    >
      {fieldsStack}
    </Form>
  );
}) as <T extends ICustomerQuoteUpsertForm | IDuplicateQuoteForm>(
  props: IProps<T> & { ref?: Ref<HTMLFormElement> }
) => JSX.Element;
